import { dedupeIntegration as coreDedupeIntegration } from '@sentry/core';
import type { Event, EventHint, Integration } from '@sentry/core';

/**
 * Deduplication filter.
 *
 * @deprecated 仅保留给依赖 `fuzzyMatch` 的 1.x 历史用法，将在 2.0 移除。
 * 新代码请使用 `dedupeIntegration()`，它直接复用 `@sentry/core` 官方实现。
 */
export class Dedupe implements Integration {
  /**
   * @inheritDoc
   */
  public static id: string = 'Dedupe';

  /**
   * @inheritDoc
   */
  public name: string = Dedupe.id;

  /**
   * @inheritDoc
   */
  private _previousEvent?: Event;
  private readonly _fuzzyMatch: boolean;

  constructor(options: { fuzzyMatch?: boolean } = {}) {
    this._fuzzyMatch = !!options.fuzzyMatch;
  }

  /**
   * @inheritDoc
   */
  public setupOnce(): void {
    // This integration doesn't need setup
  }

  /**
   * @inheritDoc
   */
  public processEvent(currentEvent: Event, _hint?: EventHint): Event | null {
    // We want to ignore any non-error type events, e.g. transactions or replays
    // These should never be deduped, and also not be compared against as _previousEvent.
    if (currentEvent.type) {
      return currentEvent;
    }

    // Juuust in case something goes wrong
    try {
      if (this._shouldDropEvent(currentEvent, this._previousEvent)) {
        return null;
      }
    } catch (_oO) {
      this._previousEvent = this._cloneEventForComparison(currentEvent);
      return currentEvent;
    }

    this._previousEvent = this._cloneEventForComparison(currentEvent);
    return currentEvent;
  }

  /** JSDoc */
  private _shouldDropEvent(currentEvent: Event, previousEvent?: Event): boolean {
    if (!previousEvent) {
      return false;
    }

    if (this._isSameMessageEvent(currentEvent, previousEvent)) {
      return true;
    }

    if (this._isSameExceptionEvent(currentEvent, previousEvent)) {
      return true;
    }

    return false;
  }

  /** JSDoc */
  private _isSameMessageEvent(currentEvent: Event, previousEvent: Event): boolean {
    const currentMessage = currentEvent.message;
    const previousMessage = previousEvent.message;

    // If neither event has a message property, they were both exceptions, so bail out
    if (!currentMessage && !previousMessage) {
      return false;
    }

    // If only one event has a message property, the events are not the same
    if ((currentMessage && !previousMessage) || (!currentMessage && previousMessage)) {
      return false;
    }

    if (currentMessage !== previousMessage) {
      return false;
    }

    if (!this._isSameFingerprint(currentEvent, previousEvent)) {
      return false;
    }

    if (!this._isSameStacktrace(currentEvent, previousEvent)) {
      return false;
    }

    return true;
  }

  /** JSDoc */
  private _isSameExceptionEvent(currentEvent: Event, previousEvent: Event): boolean {
    const currentException = this._getExceptionFromEvent(currentEvent);
    const previousException = this._getExceptionFromEvent(previousEvent);

    if (!currentException || !previousException) {
      return false;
    }

    if (currentException.type !== previousException.type) {
      return false;
    }

    if (this._fuzzyMatch) {
      if (!fuzzyValueMatch(currentException.value, previousException.value)) {
        return false;
      }
    } else {
      if (currentException.value !== previousException.value) {
        return false;
      }
    }

    if (!this._isSameFingerprint(currentEvent, previousEvent)) {
      return false;
    }

    if (!this._isSameStacktrace(currentEvent, previousEvent)) {
      return false;
    }

    return true;
  }

  /** JSDoc */
  private _isSameStacktrace(currentEvent: Event, previousEvent: Event): boolean {
    let currentFrames = this._getFramesFromEvent(currentEvent);
    let previousFrames = this._getFramesFromEvent(previousEvent);

    // If neither event has a stacktrace, they are assumed to be the same
    if (!currentFrames && !previousFrames) {
      return true;
    }

    // If only one event has a stacktrace, but not the other one, they are not the same
    if ((currentFrames && !previousFrames) || (!currentFrames && previousFrames)) {
      return false;
    }

    currentFrames = currentFrames as any[];
    previousFrames = previousFrames as any[];

    // If number of frames differ, they are not the same
    if (previousFrames.length !== currentFrames.length) {
      return false;
    }

    // Otherwise, compare the frames
    for (let i = 0; i < previousFrames.length; i++) {
      const frameA = previousFrames[i];
      const frameB = currentFrames[i];

      if (
        frameA.filename !== frameB.filename ||
        frameA.lineno !== frameB.lineno ||
        frameA.colno !== frameB.colno ||
        frameA.function !== frameB.function
      ) {
        return false;
      }
    }

    return true;
  }

  /** JSDoc */
  private _isSameFingerprint(currentEvent: Event, previousEvent: Event): boolean {
    let currentFingerprint = currentEvent.fingerprint;
    let previousFingerprint = previousEvent.fingerprint;

    // If neither event has a fingerprint, they are assumed to be the same
    if (!currentFingerprint && !previousFingerprint) {
      return true;
    }

    // If only one event has a fingerprint, but not the other one, they are not the same
    if (
      (currentFingerprint && !previousFingerprint) ||
      (!currentFingerprint && previousFingerprint)
    ) {
      return false;
    }

    currentFingerprint = currentFingerprint as string[];
    previousFingerprint = previousFingerprint as string[];

    // Otherwise, compare the fingerprints
    try {
      return !!(currentFingerprint.join('') === previousFingerprint.join(''));
    } catch (_oO) {
      return false;
    }
  }

  /** JSDoc */
  private _getExceptionFromEvent(event: Event): any {
    return event.exception && event.exception.values && event.exception.values[0];
  }

  /** 浅拷贝事件的关键去重字段 */
  private _cloneEventForComparison(event: Event): Event {
    return {
      message: event.message,
      fingerprint: event.fingerprint ? [...event.fingerprint] : undefined,
      exception: event.exception
        ? {
            values: event.exception.values?.map((v) => ({
              type: v.type,
              value: v.value,
              stacktrace: v.stacktrace
                ? {
                    frames: v.stacktrace.frames?.map((f) => ({ ...f })),
                  }
                : undefined,
            })),
          }
        : undefined,
    } as Event;
  }

  /** JSDoc */
  private _getFramesFromEvent(event: Event): any[] | undefined {
    const exception = event.exception;

    if (exception) {
      try {
        // @ts-expect-error Object could be undefined
        return exception.values[0].stacktrace.frames;
      } catch (_oO) {
        // ignore
      }
    }

    return undefined;
  }
}

/**
 * Dedupe integration
 */
export const dedupeIntegration = (): Integration => coreDedupeIntegration();

/**
 * 模糊匹配：忽略错误消息中的动态部分（时间戳、UUID、数字 ID 等）
 */
const DYNAMIC_PATTERNS =
  /\d{10,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/g;

function fuzzyValueMatch(a: string | undefined, b: string | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const normalizedA = a.replace(DYNAMIC_PATTERNS, '<dynamic>');
  const normalizedB = b.replace(DYNAMIC_PATTERNS, '<dynamic>');
  return normalizedA === normalizedB;
}
